import PropTypes from 'prop-types'
import {
    Box,
    Breadcrumbs,
    Button,
    Stack,
    Typography,
} from '@mui/material'

import './Header.scss'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react/dist/iconify.js'

const renderBreadcrumbItem = (item, index, items, currentItemSx) => {
    const isLast = item.current || index === items.length - 1
    const key = item.key || `${item.label}-${index}`

    if (typeof item.render === 'function') {
        return (
            <Box key={key}>
                <div className='app-header-item'>
                    {item.render({ item, index, isLast })}
                </div>
            </Box>
        )
    }

    if (isLast) {
        return (
            <div key={key} className='app-header-item-last'>
                {item.label}
            </div>
        )
    }

    if (item.onClick) {
        return (
            <Link
                key={key}
                component="button"
                underline="hover"
                color="inherit"
                onClick={item.onClick}
                sx={item.sx}
                {...item.linkProps}
            >
                {item.label}
            </Link>
        )
    }

    if (item.to) {
        return (
            <Link
                key={key}
                to={item.to}
                underline="none"
                color="inherit"
                sx={item.sx}
                {...item.linkProps}
            >
                {item.label}
            </Link>
        )
    }

    if (item.href) {
        return (
            <Link
                key={key}
                href={item.href}
                underline="none"
                color="inherit"
                sx={item.sx}
                {...item.linkProps}
            >
                {item.label}
            </Link>
        )
    }

    return (
        <Typography key={key} color="text.secondary" sx={item.sx}>
            {item.label}
        </Typography>
    )
}

export default function Header({
    items,
    showBack,
    onBack,
    backLabel,
    backDisabled,
    backButtonProps,
    separator,
    maxItems,
    ariaLabel,
    sx,
    breadcrumbsSx,
    currentItemSx,
}) {
    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            className="app-header-breadcrumb"
            sx={sx}
        >
            {showBack && (
                <div className="app-header-breadcrumb__back" onClick={onBack} disabled={backDisabled}>
                    <Icon icon="ph:arrow-left" height={16} /> {backLabel}
                </div>
            )}

            <Breadcrumbs
                separator={separator}
                maxItems={maxItems}
                aria-label={ariaLabel}
                className="app-header-breadcrumb__trail"
                sx={breadcrumbsSx}
            >
                {items.map((item, index) => renderBreadcrumbItem(
                    item,
                    index,
                    items,
                    currentItemSx,
                ))}
            </Breadcrumbs>
        </Stack>
    )
}

Header.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.node,
        href: PropTypes.string,
        to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        onClick: PropTypes.func,
        current: PropTypes.bool,
        sx: PropTypes.object,
        linkProps: PropTypes.object,
        render: PropTypes.func,
    })),
    showBack: PropTypes.bool,
    onBack: PropTypes.func,
    backLabel: PropTypes.node,
    backDisabled: PropTypes.bool,
    backButtonProps: PropTypes.object,
    separator: PropTypes.node,
    maxItems: PropTypes.number,
    ariaLabel: PropTypes.string,
    sx: PropTypes.object,
    breadcrumbsSx: PropTypes.object,
    currentItemSx: PropTypes.object,
}

Header.defaultProps = {
    items: [],
    showBack: false,
    onBack: undefined,
    backLabel: 'Back',
    backDisabled: false,
    backButtonProps: {},
    separator: '/',
    maxItems: 8,
    ariaLabel: 'breadcrumb',
    sx: undefined,
    breadcrumbsSx: undefined,
    currentItemSx: undefined,
}
